import styled, {css} from "styled-components";
import {Colors} from "../../Styles/Colors";
import React from "react";

type PlayerStatus = "alive" | "injured" | "dead";


const Button = styled.button`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  height: 50px;
  padding: 0 24px;

  background: transparent;
  border: 0;
  color: #f5c18d;

  font-family: "Courier New", monospace;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.5px;
  cursor: pointer;
  isolation: isolate;

  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.2);
  }

  &:active {
    filter: brightness(0.9);
  }

  &:focus-visible {
    outline: 2px solid #ffb46b;
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Frame = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: visible;
  pointer-events: none;
`;

const Content = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

export const DataPadButton = ({
	children, icon, variant = "primary", onClick, disabled, ...props }
	  :{ children: React.ReactNode, icon: React.ReactNode, variant?: "primary" | "secondary",
		onClick?: () => void,
		disabled?: boolean, props?: any }) => {
	const primary = variant === "primary";

	const border = primary ? "#F1A052" : "#A3693E";
	const innerBorder = primary ? "#D57A2B" : "#85502D";
	const background = primary ? "#55270F" : "#32190D";

	return (
		<Button type="button" {...props} onClick={onClick} disabled={disabled}>
			<Frame
				viewBox="0 0 340 56"
				preserveAspectRatio="none"
				aria-hidden="true"
			>
				<defs>
					<linearGradient
						id={`frame-fill-${variant}`}
						x1="0"
						y1="0"
						x2="1"
						y2="1"
					>
						<stop offset="0%" stopColor={background} />
						<stop offset="100%" stopColor="#241107" />
					</linearGradient>
				</defs>

				{/* Äußerer technischer Rahmen */}
				<path
					d="
            M 8 1
            H 331
            L 339 9
            V 47
            L 331 55
            H 8
            L 1 48
            V 9
            Z
          "
					fill={border}
					stroke={border}
					strokeWidth="1"
				/>

				{/* Innerer Rahmen mit 3px Abstand */}
				<path
					d="
            M 11 4
            H 329
            L 336 11
            V 45
            L 329 52
            H 11
            L 4 45
            V 11
            Z
          "
					fill={`url(#frame-fill-${variant})`}
					stroke={innerBorder}
					strokeWidth="1"
				/>

				{/* Verstärkte linke obere Ecke */}
				<path
					d="M 5 19 V 9 L 12 3 H 37"
					fill="none"
					stroke="#F5A75C"
					strokeWidth="2"
				/>

				{/* Kurze obere Leiterbahn */}
				<path
					d="M 15 8 H 54"
					fill="none"
					stroke="#E68A38"
					strokeWidth="0.8"
					opacity="0.7"
				/>

				{/* Untere rechte Eckverzierung */}
				<path
					d="M 304 53 H 330 L 337 46"
					fill="none"
					stroke="#C77732"
					strokeWidth="1.2"
				/>

				{/* Kleine technische Einkerbung */}
				<path
					d="M 7 42 V 47 L 12 52 H 29"
					fill="none"
					stroke="#B7662B"
					strokeWidth="0.8"
				/>
			</Frame>

			<Content>
				{icon}
				{children}
			</Content>
		</Button>
	);
}


export const Styles = {
	Page: styled.div`
        padding: 28px;
        color: ${Colors.raw.terminal_light};
	`,

	Header: styled.div`
        display: block;
        justify-content: space-between;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 26px;

        @media (max-width: 900px) {
            flex-direction: column;
        }
	`,

	TitleBlock: styled.div`
        text-align: left;
	`,

	Eyebrow: styled.div`
        color: ${Colors.raw.neon_orange};
        letter-spacing: 3px;
        text-transform: uppercase;
        font-size: 12px;
        margin-bottom: 8px;
	`,

	Title: styled.h1`
        margin: 0;
        font-size: clamp(28px, 5vw, 52px);
        line-height: 1;
        text-transform: uppercase;
        text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subtitle: styled.div`
        margin-top: 10px;
        opacity: 0.75;
        max-width: 660px;
	`,

	CommandPanel: styled.div`
		width: 100%;
        display: grid;
        gap: 12px;
		grid-template-columns: 1fr 1fr;
		justify-content: space-between;
		padding-bottom: 12px;
		
		@media (max-width: 900px) {
            grid-template-columns: 1fr;
        }
	`,

	Panel: styled.div`
        //border: 1px solid rgba(255, 153, 51, 0.45);
        //background: linear-gradient(135deg, rgba(255, 120, 0, 0.16), rgba(255, 255, 255, 0.03)),
        rgba(0, 0, 0, 0.25);
        //box-shadow: 0 0 22px rgba(255, 122, 0, 0.12);
        padding: 25px 16px 25px 16px;
        //clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px));

        position: relative;
        width: 100%;
        max-width: 100%;
        //height: 130px;
        box-sizing: border-box;
        isolation: isolate;
        &::before {
            content: "";
            position: absolute;
            inset: 0;
            z-index: -1;

            background: linear-gradient(
                    180deg,
                    #b45b19 0%,
                    #e27a21 45%,
                    #8a3c0f 100%
            );

            clip-path: polygon(
                    4% 0,
                    44% 0,
                    47% 7%,
                    96% 7%,
                    100% 16%,
                    100% 87%,
                    98% 95%,
                    4% 95%,
                    0 84%,
                    0 17%
            );
        }

        /* Dunkle Innenfläche mit abgeschrägten Ecken */

        &::after {
            content: "";
            position: absolute;
            inset: 1px;
            z-index: -1;

            background: linear-gradient(
                    135deg,
                    #3b1d0c 0%,
                    #2b1409 55%,
                    #231007 100%
            );

            clip-path: polygon(
                    4% 0,
                    44% 0,
                    47% 7%,
                    96% 7%,
                    100% 16%,
                    100% 87%,
                    98% 95%,
                    4% 95%,
                    0 84%,
                    0 17%
            );
        }
	`,

	PanelTitle: styled.div`
        display: block;
        margin-bottom: 8px;

		color: ${Colors.raw.neon_orange};
        font-family: "Courier New", monospace;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 2.2px;
        line-height: 17px;
        text-transform: uppercase;

        text-shadow: 0 0 8px rgba(230, 112, 26, 0.22);
	`,

	FormRow: styled.div`
        display: grid;
        grid-template-columns: 1fr 110px;
        gap: 10px;
        margin-bottom: 10px;

        @media (max-width: 420px) {
            grid-template-columns: 1fr;
        }
	`,

	Input: styled.input`
        width: 100%;
        box-sizing: border-box;
        border: 1px solid rgba(255, 255, 255, 0.22);
        background: rgba(0, 0, 0, 0.35);
        color: ${Colors.raw.terminal_light};
        padding: 11px 12px;
        outline: none;

        &:focus {
            border-color: ${Colors.raw.neon_orange};
            box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
        }
	`,

	Select: styled.select`
        width: 100%;
        box-sizing: border-box;
        border: 1px solid rgba(255, 255, 255, 0.22);
        background: rgba(0, 0, 0, 0.55);
        color: ${Colors.raw.terminal_light};
        padding: 11px 12px;
        outline: none;

        &:focus {
            border-color: ${Colors.raw.neon_orange};
            box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
        }
	`,
	DataPadButton: DataPadButton,

	Button: styled.button<{ $variant?: "danger" | "success" | "ghost" | "locked" }>`
        border: 1px solid ${Colors.raw.neon_orange};
        background: rgba(255, 122, 0, 0.18);
        color: ${Colors.raw.terminal_light};
        padding: 11px 14px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-weight: 700;

        @keyframes dangerButtonBlink {
            0%, 100% {
                box-shadow: 0 0 10px rgba(255, 40, 40, 0.18);
                filter: brightness(1);
            }

            50% {
                box-shadow: 0 0 24px rgba(255, 40, 40, 0.65);
                filter: brightness(1.35);
            }
        }

        &:hover {
            background: rgba(255, 122, 0, 0.34);
            box-shadow: 0 0 18px rgba(255, 122, 0, 0.22);
        }

        ${p => p.$variant === "danger" && css`
            border-color: #ff4b4b;
            background: rgba(255, 40, 40, 0.18);
            animation: dangerButtonBlink 2s ease-in-out infinite;

            &:hover {
                background: rgba(255, 40, 40, 0.35);
                box-shadow: 0 0 18px rgba(255, 40, 40, 0.24);
            }
        `}

        ${p => p.$variant === "success" && css`
            border-color: #43ff9d;
            background: rgba(67, 255, 157, 0.12);

            &:hover {
                background: rgba(67, 255, 157, 0.24);
                box-shadow: 0 0 18px rgba(67, 255, 157, 0.2);
            }
        `}
	`,

	CurrentPlayer: styled.div`
        display: grid;
        gap: 8px;
	`,

	CurrentPlayerName: styled.div`
        font-size: 22px;
        font-weight: 800;
	`,

	CurrentPlayerMeta: styled.div`
        opacity: 0.75;
        font-size: 13px;
	`,

	StatusButtons: styled.div`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin-top: 8px;

        @media (max-width: 420px) {
            grid-template-columns: 1fr;
        }
	`,

	StatusButton: styled.button<{ $active: boolean; $tone: PlayerStatus; $disabled?: boolean }>`
        border: 1px solid ${p => {
		if (p.$active && p.$tone === "alive") return "#43ff9d";
		if (p.$active && p.$tone === "injured") return "#ffd166";
		if (p.$active && p.$tone === "dead") return "#ff4b4b";
		return "rgba(255,255,255,0.18)";
	}};
        background: ${p => p.$active ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.18)"};
        color: ${Colors.raw.terminal_light};
        padding: 10px;
        cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
        opacity: ${p => p.$disabled ? 0.45 : 1};

        &:hover {
            border-color: ${p => p.$disabled ? "rgba(255,255,255,0.18)" : Colors.raw.neon_orange};
        }
	`,

	Overview: styled.div`
        display: grid;
        grid-template-columns: repeat(6, minmax(130px, 1fr));
        gap: 14px;
        margin-bottom: 24px;

        @media (max-width: 1000px) {
            grid-template-columns: repeat(3, minmax(140px, 1fr));
        }

        @media (max-width: 550px) {
            grid-template-columns: repeat(2, minmax(140px, 1fr));
        }
	`,

	StatCard: styled.div<{ $alert?: boolean }>`
        border: 1px solid rgba(255, 255, 255, 0.16);
        background: rgba(255, 255, 255, 0.045);
        padding: 16px;
        text-align: center;
		gap: 10px;
		position: relative;
        @keyframes statCardAlertPulse {
            0%, 100% {
                background: rgba(255, 255, 255, 0.045);
                border-color: rgba(255, 75, 75, 0.35);
                box-shadow: 0 0 10px rgba(255, 40, 40, 0.08);
            }

            50% {
                background: rgba(255, 40, 40, 0.16);
                border-color: rgba(255, 75, 75, 0.7);
                box-shadow: 0 0 22px rgba(255, 40, 40, 0.22);
            }
        }

        ${p => p.$alert && css`
			animation: statCardAlertPulse 2.2s ease-in-out infinite;
		`}
	`,

	StatLabel: styled.div`
        opacity: 0.65;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 1.5px;
	`,

	StatValue: styled.div<{ $tone?: "green" | "yellow" | "red" | "orange" }>`
        font-size: 30px;
        font-weight: 800;
        margin-top: 8px;
        color: ${p => {
			if (p.$tone === "green") return "#43ff9d";
			if (p.$tone === "yellow") return "#ffd166";
			if (p.$tone === "red") return "#ff4b4b";
			return Colors.raw.neon_orange;
		}};
	`,
	StatIcon: styled.svg`
		position: absolute;
		top: 10px;
		transform: translateX(-50%);
		color: ghostwhite;
		width: 60px;
		height: 60px;
		saturate: 10%;
		opacity: .1;
	`,

	Grid: styled.div`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
        gap: 18px;
	`,

	SquadCard: styled.div<{ $supportRequested: boolean; $isOwnSquad: boolean }>`
        position: relative;
        overflow: hidden;
        border: 1px solid ${p => {
            if (p.$supportRequested) return "#ff4b4b";
            if (p.$isOwnSquad) return "#43ff9d";
            return "rgba(255, 153, 51, 0.38)";
        }};
        background: ${p => p.$supportRequested
                ? `radial-gradient(circle at top right, rgba(255, 40, 40, 0.30), transparent 38%),
            linear-gradient(180deg, rgba(255, 40, 40, 0.16), rgba(255, 255, 255, 0.018))`
                : `radial-gradient(circle at top right, rgba(255, 122, 0, 0.18), transparent 36%),
            linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018))`};
        box-shadow: ${p => {
            if (p.$supportRequested) return "0 0 26px rgba(255, 40, 40, 0.23)";
            if (p.$isOwnSquad) return "0 0 24px rgba(67, 255, 157, 0.16)";
            return "0 0 20px rgba(255, 122, 0, 0.10)";
        }};
        padding: 18px;
        text-align: left;
        clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%);

        @keyframes squadCardSupportBlink {
            0%, 100% {
                filter: brightness(1);
                box-shadow: 0 0 22px rgba(255, 40, 40, 0.22);
            }

            50% {
                filter: brightness(1.18);
                box-shadow: 0 0 34px rgba(255, 40, 40, 0.42);
            }
        }

        ${p => p.$supportRequested && css`
                animation: squadCardSupportBlink 2.2s ease-in-out infinite;
            `}

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            height: 3px;
            width: 100%;
            background: ${p => {
                if (p.$supportRequested) return "#ff4b4b";
                if (p.$isOwnSquad) return "#43ff9d";
                return Colors.raw.neon_orange;
            }};
        }
	`,

	SquadTop: styled.div`
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 16px;
	`,

	SquadName: styled.h2`
        margin: 0;
        font-size: 22px;
	`,

	CallSign: styled.div`
        margin-top: 4px;
        color: ${Colors.raw.neon_orange};
        font-size: 13px;
        letter-spacing: 2px;
        text-transform: uppercase;
	`,
	SquadEditForm: styled.div`
            display: grid;
            gap: 8px;
            min-width: min(100%, 280px);
	`,

	SquadEditActions: styled.div`
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
	`,

	Badge: styled.div<{ $danger?: boolean; $success?: boolean }>`
        border: 1px solid ${p => {
		if (p.$danger) return "#ff4b4b";
		if (p.$success) return "#43ff9d";
		return "rgba(255,255,255,0.22)";
	}};
        color: ${p => {
		if (p.$danger) return "#ff8c8c";
		if (p.$success) return "#8dffc5";
		return Colors.raw.terminal_light;
	}};
        background: ${p => {
		if (p.$danger) return "rgba(255, 40, 40, 0.14)";
		if (p.$success) return "rgba(67, 255, 157, 0.10)";
		return "rgba(255,255,255,0.06)";
	}};
        padding: 7px 10px;
        font-size: 12px;
        text-transform: uppercase;
        white-space: nowrap;
	`,

	StatusGrid: styled.div`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin-bottom: 14px;
	`,

	StatusBox: styled.div<{ $tone: PlayerStatus, $myStatus?: boolean }>`
        border: 2px solid ${ p => p.$myStatus ? "rgba(155, 155, 10, 0.8)" : "rgba(255, 255, 255, 0.16)"};
        background: rgba(0, 0, 0, 0.22);
        padding: 12px;
        text-align: center;
		cursor: ${ p => p.$myStatus ? "pointer" : "default" };

        svg {
            color: ${p => {
		if (p.$tone === "alive") return "#43ff9d";
		if (p.$tone === "injured") return "#ffd166";
		return "#ff4b4b";
	}};
            margin-bottom: 6px;
        }
	`,

	StatusNumber: styled.div<{ $tone: PlayerStatus }>`
        color: ${p => {
		if (p.$tone === "alive") return "#43ff9d";
		if (p.$tone === "injured") return "#ffd166";
		return "#ff4b4b";
	}};
        font-size: 26px;
        font-weight: 800;
        text-align: center;
	`,

	StatusLabel: styled.div`
        opacity: 0.7;
        font-size: 11px;
        text-transform: uppercase;
	`,

	ProgressOuter: styled.div`
        height: 8px;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.14);
        overflow: hidden;
        margin-bottom: 18px;
	`,

	ProgressInner: styled.div<{ $progress: number }>`
        height: 100%;
        width: ${p => p.$progress}%;
        background: linear-gradient(90deg, ${Colors.raw.neon_orange}, #43ff9d);
        box-shadow: 0 0 14px rgba(67, 255, 157, 0.35);
	`,

	SectionTitle: styled.div<{ onClick: () => void }>`
        margin: 16px 0 10px;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        color: ${Colors.raw.neon_orange};
        font-size: 13px;
		cursor: ${ p => p.onClick ? "pointer" : "default" };
	`,

	MemberList: styled.div`
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
	`,

	MemberChip: styled.div<{ $status: PlayerStatus; $active?: boolean }>`
        border: 1px solid ${p => {
		if (p.$active) return "#43ff9d";
		if (p.$status === "alive") return "rgba(67, 255, 157, 0.35)";
		if (p.$status === "injured") return "rgba(255, 209, 102, 0.35)";
		return "rgba(255, 75, 75, 0.35)";
	}};
        background: ${p => p.$active ? "rgba(67, 255, 157, 0.12)" : "rgba(0,0,0,0.20)"};
        padding: 7px 9px;
        font-size: 12px;
	`,

	ObjectiveList: styled.div`
        display: flex;
        flex-direction: column;
        gap: 8px;
	`,

	Objective: styled.button<{ $done: boolean; $locked: boolean }>`
        width: 100%;
        border: 1px solid ${p => {
		if (p.$locked) return "rgba(255,255,255,0.10)";
		if (p.$done) return "rgba(67, 255, 157, 0.45)";
		return "rgba(255,255,255,0.16)";
	}};
        background: ${p => {
		if (p.$locked) return "rgba(255,255,255,0.035)";
		if (p.$done) return "rgba(67, 255, 157, 0.10)";
		return "rgba(0,0,0,0.18)";
	}};
        color: ${Colors.raw.terminal_light};
        padding: 10px;
        cursor: ${p => p.$locked ? "not-allowed" : "pointer"};
        display: grid;
        grid-template-columns: 24px 1fr auto;
        gap: 8px;
        align-items: center;
        text-align: left;
        opacity: ${p => p.$locked ? 0.55 : 1};

        span {
            text-decoration: ${p => p.$done ? "line-through" : "none"};
            opacity: ${p => p.$done ? 0.62 : 1};
        }

        svg {
            color: ${p => p.$done ? "#43ff9d" : "rgba(255,255,255,0.35)"};
        }

        &:hover {
            border-color: ${p => p.$locked ? "rgba(255,255,255,0.10)" : Colors.raw.neon_orange};
        }
	`,
	ObjectiveDeleteButton: styled.button`
            border: 1px solid rgba(255, 75, 75, 0.55);
            background: rgba(255, 40, 40, 0.12);
            color: #ff8c8c;
            width: 28px;
            height: 28px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            &:hover {
                border-color: #ff4b4b;
                background: rgba(255, 40, 40, 0.28);
                box-shadow: 0 0 12px rgba(255, 40, 40, 0.22);
            }
	`,

	AddObjective: styled.div`
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
        margin-top: 10px;
	`,

	Actions: styled.div`
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: 16px;
	`,

	LockedNotice: styled.div`
        margin-top: 10px;
        opacity: 0.6;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 7px;
	`,

	EmptyState: styled.div`
        border: 1px dashed rgba(255, 255, 255, 0.25);
        padding: 30px;
        text-align: center;
        opacity: 0.75;
	`,
};